import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  Paper,
  Chip,
  LinearProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Divider,
  useTheme,
  Snackbar,
  MenuItem,
  TextField as MUITextField
} from '@mui/material';
import {
  Upload as UploadIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  CloudUpload as CloudUploadIcon,
  Assessment as AssessmentIcon,
  Security as SecurityIcon,
  Error as ErrorIcon,
  AttachFile as AttachFileIcon
} from '@mui/icons-material';
import { notifyPRSubmitted, notifyVerificationApproved } from '../../utils/notificationHelpers';

// API Base URL
import { getApiBaseUrl } from '../../utils/apiConfig';
const API_BASE_URL = `${getApiBaseUrl()}/api`;

// Phase 1: PR Submission & Verification Components

interface PRUploadInterfaceProps {
  onUploadSuccess?: (data: any) => void;
  onContentChange?: (content: string) => void;
  initialContent?: string;
}

// PR Upload Interface Component
export const PRUploadInterface: React.FC<PRUploadInterfaceProps> = ({ onUploadSuccess, onContentChange, initialContent }) => {
  const theme = useTheme();
  const [dragActive, setDragActive] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [thumbnailFile, setThumbnailFile] = useState<File | null>(null);
  const [thumbnailPreview, setThumbnailPreview] = useState<string | null>(null);
  const [prContent, setPrContent] = useState(initialContent || '');
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const thumbnailInputRef = useRef<HTMLInputElement>(null);

  // Update content when initialContent changes (e.g., when navigating back to step 1)
  useEffect(() => {
    if (initialContent !== undefined) {
      setPrContent(initialContent);
    }
  }, [initialContent]);

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const files = Array.from(e.dataTransfer.files);
      setUploadedFiles(prev => [...prev, ...files]);
    }
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const files = Array.from(e.target.files);
      setUploadedFiles(prev => [...prev, ...files]);
    }
  };

  const handleThumbnailInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setThumbnailFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setThumbnailPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeFile = (index: number) => {
    setUploadedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleContentChange = (content: string) => {
    setPrContent(content);
    if (onContentChange) {
      onContentChange(content);
    }
  };

  const processFiles = async () => {
    if (uploadedFiles.length === 0 && !prContent.trim()) {
      setError('Please upload files or enter PR content');
      return;
    }

    setUploading(true);
    setError(null);

    try {
      const formData = new FormData();
      
      // Add files to form data
      uploadedFiles.forEach((file) => {
        formData.append('files', file);
      });

      // Add thumbnail image if available
      if (thumbnailFile) {
        formData.append('thumbnail', thumbnailFile);
      }

      // Add text content if available
      if (prContent.trim()) {
        formData.append('content', prContent);
      }

      // Get auth token from localStorage
      const token = localStorage.getItem('token');
      
      const response = await axios.post(`${API_BASE_URL}/pr/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${token}`
        }
      });

      setSuccess('Files processed successfully!');
      
      if (onUploadSuccess) {
        onUploadSuccess(response.data);
      }

    } catch (err: any) {
      console.error('Upload error:', err);
      setError(err.response?.data?.message || 'Failed to upload files. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        mb: 2,
        backgroundColor: theme.palette.background.paper,
        borderRadius: '12px',
        overflow: 'hidden',
        border: `1px solid ${theme.palette.divider}`,
        transition: 'box-shadow 0.2s',
        '&:hover': {
          boxShadow: theme.palette.mode === 'dark' ? '0 2px 12px rgba(0,0,0,0.5)' : '0 2px 12px rgba(0,0,0,0.08)'
        }
      }}
    >
      {/* Header */}
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: '8px',
            backgroundColor: theme.palette.primary.main,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0
          }}
        >
          <CloudUploadIcon sx={{ fontSize: 20, color: theme.palette.primary.contrastText }} />
        </Box>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.text.primary, mb: 0.5, fontSize: '16px' }}>
            Upload PR Files
          </Typography>
          <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontSize: '13px' }}>
            Drag and drop files or browse from your device
          </Typography>
        </Box>
      </Box>

      <Divider />

      <Box sx={{ p: 2 }}>
        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Success Alert */}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        {/* Drag and Drop Area */}
        <Paper
          elevation={0}
          sx={{
            p: 3,
            textAlign: 'center',
            border: dragActive ? `2px dashed ${theme.palette.primary.main}` : `2px dashed ${theme.palette.divider}`,
            backgroundColor: dragActive 
              ? (theme.palette.mode === 'dark' ? 'rgba(58, 79, 237, 0.1)' : '#f8f9ff')
              : (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : '#fafafa'),
            cursor: 'pointer',
            borderRadius: '8px',
            mb: 2,
            transition: 'all 0.2s'
          }}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
        >
          <CloudUploadIcon sx={{ fontSize: 36, color: theme.palette.primary.main, mb: 1 }} />
          <Typography variant="body1" sx={{ fontWeight: 500, color: theme.palette.text.primary, mb: 0.5 }}>
            Drop files here
          </Typography>
          <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontSize: '13px' }}>
            Supports .docx, .pdf, .txt files
          </Typography>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept=".docx,.pdf,.txt"
            onChange={handleFileInput}
            style={{ display: 'none' }}
          />
        </Paper>

        {/* Thumbnail Upload */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.text.primary, mb: 1 }}>
            Thumbnail Image (PPT-style cover)
          </Typography>
          <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontSize: '12px', mb: 1 }}>
            Recommended: 16:9 aspect ratio (e.g., 1280x720). Supported formats: JPG, PNG, WEBP.
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Button
              variant="outlined"
              size="small"
              onClick={() => thumbnailInputRef.current?.click()}
              startIcon={<UploadIcon />}
              sx={{ textTransform: 'none' }}
            >
              {thumbnailFile ? 'Change Thumbnail' : 'Upload Thumbnail'}
            </Button>
            {thumbnailPreview && (
              <Box
                sx={{
                  width: 160,
                  height: 90,
                  borderRadius: '8px',
                  overflow: 'hidden',
                  border: `1px solid ${theme.palette.divider}`,
                  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : '#fafafa'
                }}
              >
                <img
                  src={thumbnailPreview}
                  alt="Thumbnail preview"
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              </Box>
            )}
            <input
              ref={thumbnailInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              style={{ display: 'none' }}
              onChange={handleThumbnailInput}
            />
          </Box>
        </Box>

        {/* Uploaded Files */}
        {uploadedFiles.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.text.primary, mb: 1 }}>
              Uploaded Files
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {uploadedFiles.map((file, index) => (
                <Chip
                  key={index}
                  label={file.name}
                  onDelete={() => removeFile(index)}
                  size="small"
                  sx={{
                    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : '#f5f5f5',
                    border: `1px solid ${theme.palette.divider}`,
                    fontWeight: 500,
                    '&:hover': {
                      backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.15)' : '#ebebeb'
                    }
                  }}
                />
              ))}
            </Box>
          </Box>
        )}

        {/* Rich Text Editor */}
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.text.primary, mb: 1 }}>
            Or Write Your PR Directly
          </Typography>
          <TextField
            multiline
            rows={12}
            fullWidth
            placeholder="Paste or type your press release here...

Include:
- Compelling headline
- Dateline (City, State - Date)
- Lead paragraph with key information
- Body paragraphs with details
- Company boilerplate
- Contact information"
            value={prContent}
            onChange={(e) => handleContentChange(e.target.value)}
            disabled={uploading}
            sx={{
              backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : '#fafafa',
              '& .MuiOutlinedInput-root': {
                borderColor: theme.palette.divider,
                fontFamily: 'monospace',
                fontSize: '14px'
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.divider
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.primary.main,
              }
            }}
            helperText={`${prContent.split(' ').length} words | ${Math.ceil(prContent.split(' ').length / 200)} min read`}
          />
        </Box>
      </Box>

      <Box sx={{ p: 2, backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : '#f9f9f9', display: 'flex', justifyContent: 'flex-end', borderTop: `1px solid ${theme.palette.divider}` }}>
        <Button
          variant="contained"
          startIcon={uploading ? null : <UploadIcon />}
          onClick={processFiles}
          disabled={uploading || (uploadedFiles.length === 0 && !prContent.trim())}
          sx={{
            backgroundColor: theme.palette.mode === 'dark' ? theme.palette.primary.main : '#000000',
            color: '#ffffff',
            fontWeight: 600,
            textTransform: 'none',
            px: 3,
            py: 1,
            fontSize: '14px',
            borderRadius: '8px',
            boxShadow: 'none',
            '&:hover': {
              backgroundColor: theme.palette.mode === 'dark' ? theme.palette.primary.dark : '#1a1a1a',
              boxShadow: 'none'
            },
            '&:disabled': {
              backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : '#f0f0f0',
              color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.3)' : '#d0d0d0'
            }
          }}
        >
          {uploading ? 'Processing...' : 'Process Files'}
        </Button>
      </Box>
    </Paper>
  );
};

// Helper function to get percentage meaning label
const getPercentageLabel = (score: number): string => {
  if (score < 59) return 'Needs Improvement';
  if (score >= 60 && score <= 70) return 'Good';
  if (score >= 71 && score <= 80) return 'Very Good';
  if (score >= 81) return 'Excellent';
  return 'N/A';
};

// Helper function to get color based on score
const getScoreColor = (score: number): string => {
  if (score < 59) return '#ffc107'; // Yellow
  if (score >= 60 && score <= 70) return '#2196f3'; // Blue
  if (score >= 71 && score <= 80) return '#4caf50'; // Green
  if (score >= 81) return '#9c27b0'; // Purple
  return '#999999'; // Gray for N/A
};

// Helper function to get background color based on score
const getScoreBackgroundColor = (score: number, isDark: boolean): string => {
  if (score < 59) return isDark ? 'rgba(255, 193, 7, 0.1)' : '#fffbea'; // Yellow
  if (score >= 60 && score <= 70) return isDark ? 'rgba(33, 150, 243, 0.1)' : '#e3f2fd'; // Blue
  if (score >= 71 && score <= 80) return isDark ? 'rgba(76, 175, 80, 0.1)' : '#e8f5e9'; // Green
  if (score >= 81) return isDark ? 'rgba(156, 39, 176, 0.1)' : '#f3e5f5'; // Purple
  return isDark ? 'rgba(255,255,255,0.03)' : '#fafafa'; // Gray
};

// AI Analysis Component
interface AIAnalysisInterfaceProps {
  prId?: string;
  content?: string;
  onAnalysisComplete?: (data: any) => void;
  onEditPR?: () => void;
}

export const AIAnalysisInterface: React.FC<AIAnalysisInterfaceProps> = ({ prId, content, onAnalysisComplete, onEditPR }) => {
  const theme = useTheme();
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisComplete, setAnalysisComplete] = useState(false);
  const [analysisData, setAnalysisData] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const hasAutoStartedRef = useRef(false);

  const runAnalysis = async () => {
    if (!prId && !content) {
      setError('No PR content available for analysis');
      return;
    }

    setIsAnalyzing(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      
      const response = await axios.post(
        `${API_BASE_URL}/pr/analyze`,
        {
          prId,
          content
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      setAnalysisData(response.data);
      setAnalysisComplete(true);

      if (onAnalysisComplete) {
        onAnalysisComplete(response.data);
      }

    } catch (err: any) {
      console.error('Analysis error:', err);
      setError(err.response?.data?.message || 'Failed to analyze content. Please try again.');
    } finally {
      setIsAnalyzing(false);
    }
  };

  // Auto-start analysis when component mounts and content is available
  useEffect(() => {
    // Only auto-start once when content becomes available
    if (!hasAutoStartedRef.current && (prId || content) && !isAnalyzing && !analysisComplete) {
      hasAutoStartedRef.current = true;
      // Use setTimeout to ensure the component is fully mounted
      setTimeout(() => {
        runAnalysis();
      }, 100);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prId, content]);

  return (
    <Paper
      elevation={0}
      sx={{
        mb: 2,
        backgroundColor: theme.palette.background.paper,
        borderRadius: '12px',
        overflow: 'hidden',
        border: `1px solid ${theme.palette.divider}`,
        transition: 'box-shadow 0.2s',
        '&:hover': {
          boxShadow: theme.palette.mode === 'dark' ? '0 2px 12px rgba(0,0,0,0.5)' : '0 2px 12px rgba(0,0,0,0.08)'
        }
      }}
    >
      {/* Header */}
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '8px',
              backgroundColor: '#F36409',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }}
          >
            <AssessmentIcon sx={{ fontSize: 20, color: '#fff' }} />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.text.primary, mb: 0.5, fontSize: '16px' }}>
              AI-Powered Analysis
            </Typography>
            <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontSize: '13px' }}>
              Get instant feedback and suggestions
            </Typography>
          </Box>
        </Box>
        
        {/* Score Legend */}
        <Box sx={{ 
          display: 'flex', 
          gap: { xs: 1, sm: 1.5 }, 
          flexWrap: 'wrap', 
          alignItems: 'center',
          backgroundColor: '#f8f9fa',
          px: 2,
          py: 1,
          borderRadius: '8px',
          border: '1px solid #e0e0e0'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Box sx={{ width: 14, height: 14, borderRadius: '50%', backgroundColor: '#ffc107', border: '2px solid #fff', boxShadow: '0 1px 3px rgba(255, 193, 7, 0.3)' }} />
            <Typography variant="caption" sx={{ fontSize: '11px', color: '#09273b', fontWeight: 600 }}>
              &lt;59%: Needs Improvement
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Box sx={{ width: 14, height: 14, borderRadius: '50%', backgroundColor: '#2196f3', border: '2px solid #fff', boxShadow: '0 1px 3px rgba(33, 150, 243, 0.3)' }} />
            <Typography variant="caption" sx={{ fontSize: '11px', color: '#09273b', fontWeight: 600 }}>
              60-70%: Good
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Box sx={{ width: 14, height: 14, borderRadius: '50%', backgroundColor: '#4caf50', border: '2px solid #fff', boxShadow: '0 1px 3px rgba(76, 175, 80, 0.3)' }} />
            <Typography variant="caption" sx={{ fontSize: '11px', color: '#09273b', fontWeight: 600 }}>
              71-80%: Very Good
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Box sx={{ width: 14, height: 14, borderRadius: '50%', backgroundColor: '#9c27b0', border: '2px solid #fff', boxShadow: '0 1px 3px rgba(156, 39, 176, 0.3)' }} />
            <Typography variant="caption" sx={{ fontSize: '11px', color: '#09273b', fontWeight: 600 }}>
              81-100%: Excellent
            </Typography>
          </Box>
        </Box>
      </Box>

      <Divider />

      <Box sx={{ p: 2 }}>
        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {!isAnalyzing && !analysisComplete && (
          <Box sx={{ p: 1.5, backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : '#fafafa', borderRadius: '8px', border: `1px solid ${theme.palette.divider}` }}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.text.primary, mb: 1 }}>
              Content Quality Score
            </Typography>
            <LinearProgress
              variant="determinate"
              value={0}
              sx={{
                height: 8,
                borderRadius: 4,
                backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : '#e0e0e0',
                '& .MuiLinearProgress-bar': {
                  backgroundColor: theme.palette.primary.main,
                }
              }}
            />
            <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.primary.main, fontSize: '13px', mt: 1 }}>
              Click "Analyze" to start
            </Typography>
          </Box>
        )}

        {isAnalyzing && (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <LinearProgress 
              sx={{
                mb: 2,
                height: 8,
                borderRadius: 4,
                backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : '#e0e0e0',
                '& .MuiLinearProgress-bar': {
                  backgroundColor: '#F36409',
                }
              }}
            />
            <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
              Analyzing content with AI...
            </Typography>
          </Box>
        )}

        {analysisComplete && analysisData && (
          <>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} md={3}>
                <Box sx={{ p: 1.5, backgroundColor: getScoreBackgroundColor(analysisData.contentQuality || 0, theme.palette.mode === 'dark'), borderRadius: '8px', border: `1px solid ${theme.palette.divider}` }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.text.primary, mb: 1 }}>
                    Content Quality
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={analysisData.contentQuality || 0}
                    sx={{
                      height: 8,
                      borderRadius: 4,
                      backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : '#e0e0e0',
                      '& .MuiLinearProgress-bar': {
                        backgroundColor: getScoreColor(analysisData.contentQuality || 0),
                      }
                    }}
                  />
                  <Typography variant="body2" sx={{ fontWeight: 600, color: getScoreColor(analysisData.contentQuality || 0), fontSize: '13px', mt: 1 }}>
                    {analysisData.contentQuality || 0}% - {getPercentageLabel(analysisData.contentQuality || 0)}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={3}>
                <Box sx={{ p: 1.5, backgroundColor: getScoreBackgroundColor(analysisData.seoScore || 0, theme.palette.mode === 'dark'), borderRadius: '8px', border: `1px solid ${theme.palette.divider}` }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.text.primary, mb: 1 }}>
                    SEO Score
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={analysisData.seoScore || 0}
                    sx={{
                      height: 8,
                      borderRadius: 4,
                      backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : '#e0e0e0',
                      '& .MuiLinearProgress-bar': {
                        backgroundColor: getScoreColor(analysisData.seoScore || 0),
                      }
                    }}
                  />
                  <Typography variant="body2" sx={{ fontWeight: 600, color: getScoreColor(analysisData.seoScore || 0), fontSize: '13px', mt: 1 }}>
                    {analysisData.seoScore || 0}% - {getPercentageLabel(analysisData.seoScore || 0)}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={3}>
                <Box sx={{ p: 1.5, backgroundColor: getScoreBackgroundColor(analysisData.readability || analysisData.readabilityScore || 0, theme.palette.mode === 'dark'), borderRadius: '8px', border: `1px solid ${theme.palette.divider}` }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.text.primary, mb: 1 }}>
                    Readability
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={analysisData.readability || analysisData.readabilityScore || 0}
                    sx={{
                      height: 8,
                      borderRadius: 4,
                      backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : '#e0e0e0',
                      '& .MuiLinearProgress-bar': {
                        backgroundColor: getScoreColor(analysisData.readability || analysisData.readabilityScore || 0),
                      }
                    }}
                  />
                  <Typography variant="body2" sx={{ fontWeight: 600, color: getScoreColor(analysisData.readability || analysisData.readabilityScore || 0), fontSize: '13px', mt: 1 }}>
                    {analysisData.readability || analysisData.readabilityScore || 0}% - {getPercentageLabel(analysisData.readability || analysisData.readabilityScore || 0)}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={3}>
                <Box sx={{ p: 1.5, backgroundColor: getScoreBackgroundColor(analysisData.contactCompleteness || 0, theme.palette.mode === 'dark'), borderRadius: '8px', border: `1px solid ${theme.palette.divider}` }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.text.primary, mb: 1 }}>
                    Contact Completeness
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={analysisData.contactCompleteness || 0}
                    sx={{
                      height: 8,
                      borderRadius: 4,
                      backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : '#e0e0e0',
                      '& .MuiLinearProgress-bar': {
                        backgroundColor: getScoreColor(analysisData.contactCompleteness || 0),
                      }
                    }}
                  />
                  <Typography variant="body2" sx={{ fontWeight: 600, color: getScoreColor(analysisData.contactCompleteness || 0), fontSize: '13px', mt: 1 }}>
                    {analysisData.contactCompleteness || 0}% - {getPercentageLabel(analysisData.contactCompleteness || 0)}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
            
            {analysisData.suggestions && analysisData.suggestions.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.text.primary, mb: 1 }}>
                  AI Suggestions for Improvement
                </Typography>
                <List dense>
                  {analysisData.suggestions.map((suggestion: string, index: number) => (
                    <ListItem key={index} sx={{ py: 0.5 }}>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <InfoIcon sx={{ fontSize: 16, color: theme.palette.primary.main }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary={suggestion}
                        primaryTypographyProps={{ fontSize: '13px' }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}

            {analysisData.keywords && analysisData.keywords.length > 0 && (
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.text.primary, mb: 1 }}>
                  Detected Keywords
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {analysisData.keywords.map((keyword: string, index: number) => (
                    <Chip
                      key={index}
                      label={keyword}
                      size="small"
                      sx={{ fontSize: '11px' }}
                    />
                  ))}
                </Box>
              </Box>
            )}
          </>
        )}
      </Box>

      <Box sx={{ p: 2, backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : '#f9f9f9', display: 'flex', justifyContent: onEditPR ? 'space-between' : 'flex-end', alignItems: 'center', borderTop: `1px solid ${theme.palette.divider}` }}>
        {onEditPR && (
          <Button
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={onEditPR}
            sx={{
              borderColor: theme.palette.primary.main,
              color: theme.palette.primary.main,
              fontWeight: 600,
              textTransform: 'none',
              px: 3,
              py: 1,
              fontSize: '14px',
              borderRadius: '8px',
              '&:hover': {
                borderColor: theme.palette.primary.dark,
                backgroundColor: theme.palette.mode === 'dark' ? 'rgba(58, 79, 237, 0.1)' : 'rgba(9, 39, 235, 0.05)'
              }
            }}
          >
            Edit PR
          </Button>
        )}
        <Button
          variant="contained"
          startIcon={<AssessmentIcon />}
          onClick={runAnalysis}
          disabled={isAnalyzing || (!prId && !content)}
          sx={{
            backgroundColor: theme.palette.mode === 'dark' ? theme.palette.primary.main : '#000000',
            color: '#ffffff',
            fontWeight: 600,
            textTransform: 'none',
            px: 3,
            py: 1,
            fontSize: '14px',
            borderRadius: '8px',
            boxShadow: 'none',
            '&:hover': {
              backgroundColor: theme.palette.mode === 'dark' ? theme.palette.primary.dark : '#1a1a1a',
              boxShadow: 'none'
            },
            '&:disabled': {
              backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : '#f0f0f0',
              color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.3)' : '#d0d0d0'
            }
          }}
        >
          {isAnalyzing ? 'Analyzing...' : analysisComplete ? 'Re-analyze Content' : 'Analyze Content'}
        </Button>
      </Box>
    </Paper>
  );
};

// Verification Component
interface VerificationStep {
  id: string;
  label: string;
  completed: boolean;
  inProgress: boolean;
  tier: number;
  documents?: string[];
}

interface VerificationInterfaceProps {
  prId?: string;
  prContent?: string;
  analysisData?: any;
  onVerificationComplete?: (data: any) => void;
}

export const VerificationInterface: React.FC<VerificationInterfaceProps> = ({ prId, prContent, analysisData, onVerificationComplete }) => {
  const theme = useTheme();
  const [isFirstPR, setIsFirstPR] = useState(true);
  const [verificationSteps, setVerificationSteps] = useState<VerificationStep[]>([
    { id: 'tier1', label: 'Stage 1: Essential Documents', completed: false, inProgress: false, tier: 1, documents: [] },
    { id: 'tier2', label: 'Stage 2: Content Validation', completed: false, inProgress: false, tier: 2, documents: [] },
    { id: 'tier3', label: 'Stage 3: Third-Party Validation (Optional)', completed: false, inProgress: false, tier: 3, documents: [] },
    { id: 'tier4', label: 'Stage 4: Digital Evidence', completed: false, inProgress: false, tier: 4, documents: [] }
  ]);
  const [error, setError] = useState<string | null>(null);
  const [uploadingDocs, setUploadingDocs] = useState<{ [key: string]: boolean }>({});
  const fileInputRefs = useRef<{ [key: string]: HTMLInputElement | null }>({});
  const [selectedDocTypes, setSelectedDocTypes] = useState<{ [key: string]: string }>({});

  // Check if it's the first PR and load verification status on component mount
  // Note: All 4 stages are always shown, regardless of first PR status
  useEffect(() => {
    const checkFirstPRStatusAndVerification = async () => {
      try {
        const token = localStorage.getItem('token');
        
        // Check if it's the first PR
        const firstPRResponse = await axios.get(`${API_BASE_URL}/pr/check-first-pr`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        const isFirst = firstPRResponse.data.isFirstPR || false;
        setIsFirstPR(isFirst);

        // Stage 1 only needs to be verified once
        // If it's not the first PR, Stage 1 should already be verified
        if (!isFirst) {
          setVerificationSteps(prevSteps => 
            prevSteps.map(step => 
              step.id === 'tier1' 
                ? { ...step, completed: true }
                : step
            )
          );
        }

        // Also check verification status for the current PR if prId is available
        if (prId) {
          try {
            const verificationResponse = await axios.get(`${API_BASE_URL}/pr/${prId}/verification/status`, {
              headers: {
                'Authorization': `Bearer ${token}`
              }
            });
            
            // Update verification steps based on the response
            if (verificationResponse.data) {
              setVerificationSteps(prevSteps => 
                prevSteps.map(step => {
                  const tierKey = `tier${step.tier}`;
                  const tierStatus = verificationResponse.data[tierKey];
                  if (tierStatus?.verified) {
                    return { ...step, completed: true, documents: tierStatus.documents || step.documents };
                  }
                  return step;
                })
              );
            }
          } catch (verificationErr: any) {
            // If the endpoint doesn't exist or returns 404, that's okay
            // We'll just proceed with the default state
            if (verificationErr.response?.status !== 404) {
              console.log('Verification status check error:', verificationErr);
            }
          }
        }
      } catch (err) {
        console.error('Error checking first PR status:', err);
        // Default to true
        setIsFirstPR(true);
      }
    };
    checkFirstPRStatusAndVerification();
  }, [prId]);

  // Fixed document lists for each tier with "Other" option
  const tierDescriptions: { [key: string]: string[] } = {
    tier1: [
      'Company registration certificates',
      'Business licenses',
      'Official letterhead samples',
      'Domain verification (email authentication)',
      'Other'
    ],
    tier2: [
      'Product images/videos with metadata',
      'Financial statements (for financial news)',
      'Clinical trial documents (for healthcare/pharma)',
      'Patent filings (for tech innovations)',
      'Legal disclaimers/certifications',
      'Executive quotes with contact verification',
      'Other'
    ],
    tier3: [
      'Expert testimonials with credentials',
      'Independent research reports',
      'Industry analyst quotes',
      'Media mentions/previous coverage',
      'Client testimonials with consent forms',
      'Partnership agreements/MoUs',
      'Other'
    ],
    tier4: [
      'Website analytics screenshots',
      'Social media verification badges',
      'LinkedIn company page verification',
      'Press kit downloads',
      'Event registration confirmations',
      'Other'
    ]
  };

  const handleDocumentUpload = async (stepId: string, files: FileList | null) => {
    if (!files || files.length === 0 || !prId) return;

    setUploadingDocs({ ...uploadingDocs, [stepId]: true });
    setError(null);

    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      
      Array.from(files).forEach(file => {
        formData.append('documents', file);
      });

      // Extract tier number from stepId (e.g., 'tier1' -> '1')
      const tier = stepId.replace('tier', '').replace('Tier', '');
      if (!tier || isNaN(Number(tier))) {
        throw new Error('Invalid tier identifier');
      }
      formData.append('tier', tier);
      const selectedType = selectedDocTypes[stepId] || 'general';
      formData.append('documentType', selectedType);

      const response = await axios.post(
        `${API_BASE_URL}/pr/${prId}/verification/documents`,
        formData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      // Update step with uploaded documents
      const newSteps = verificationSteps.map(step => {
        if (step.id === stepId) {
          return {
            ...step,
            documents: [...(step.documents || []), ...Array.from(files).map(f => f.name)],
            completed: true
          };
        }
        return step;
      });
      setVerificationSteps(newSteps);
      setUploadingDocs({ ...uploadingDocs, [stepId]: false });

      // Verify the step
      await startVerification(newSteps.findIndex(s => s.id === stepId), stepId);

      // Check if all required steps are completed after document upload (Tier 3 is optional)
      const requiredSteps = newSteps.filter(step => step.id !== 'tier3');
      const allRequiredCompleted = requiredSteps.length > 0 && requiredSteps.every(step => step.completed);
      
      if (allRequiredCompleted && onVerificationComplete) {
        onVerificationComplete(response.data);
      }

    } catch (err: any) {
      console.error('Document upload error:', err);
      setUploadingDocs({ ...uploadingDocs, [stepId]: false });
      
      let errorMessage = 'Failed to upload documents';
      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.response?.data?.error) {
        errorMessage = `${errorMessage}: ${err.response.data.error}`;
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      // Check for Cloudinary configuration error
      if (errorMessage.includes('Cloudinary') || errorMessage.includes('not configured')) {
        errorMessage = 'File upload service is not configured. Please contact support or check your environment variables.';
      }
      
      setError(errorMessage);
    }
  };

  const startVerification = async (index: number, stepId: string) => {
    const newSteps = [...verificationSteps];
    newSteps[index].inProgress = true;
    setVerificationSteps(newSteps);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      
      const response = await axios.post(
        `${API_BASE_URL}/pr/verify`,
        {
          prId,
          step: stepId
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      newSteps[index].inProgress = false;
      newSteps[index].completed = response.data.verified || false;
      setVerificationSteps([...newSteps]);

      // Check if all required steps are completed (Tier 3 is optional)
      const requiredSteps = newSteps.filter(step => step.id !== 'tier3');
      const allRequiredCompleted = requiredSteps.length > 0 && requiredSteps.every(step => step.completed);
      
      if (allRequiredCompleted && onVerificationComplete) {
        onVerificationComplete(response.data);
      }

    } catch (err: any) {
      console.error('Verification error:', err);
      newSteps[index].inProgress = false;
      setVerificationSteps([...newSteps]);
      setError(err.response?.data?.message || `Failed to verify ${newSteps[index].label}`);
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        mb: 2,
        backgroundColor: theme.palette.background.paper,
        borderRadius: '12px',
        overflow: 'hidden',
        border: `1px solid ${theme.palette.divider}`,
        transition: 'box-shadow 0.2s',
        '&:hover': {
          boxShadow: theme.palette.mode === 'dark' ? '0 2px 12px rgba(0,0,0,0.5)' : '0 2px 12px rgba(0,0,0,0.08)'
        }
      }}
    >
      {/* Header */}
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: '8px',
            backgroundColor: '#4caf50',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0
          }}
        >
          <SecurityIcon sx={{ fontSize: 20, color: '#fff' }} />
        </Box>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.text.primary, mb: 0.5, fontSize: '16px' }}>
            Multi-Stage Verification
          </Typography>
          <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontSize: '13px' }}>
            Complete verification steps to build trust
          </Typography>
        </Box>
      </Box>

      <Divider />

      <Box sx={{ p: 2 }}>
        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <List>
          {verificationSteps.map((step, index) => (
            <ListItem
              key={index}
              sx={{
                borderRadius: '8px',
                mb: 1,
                backgroundColor: step.completed 
                  ? (theme.palette.mode === 'dark' ? 'rgba(76, 175, 80, 0.1)' : '#e8f5e9')
                  : (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : '#fafafa'),
                border: `1px solid ${theme.palette.divider}`,
                transition: 'all 0.2s',
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 2,
                '&:hover': {
                  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : '#f5f5f5'
                }
              }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                {step.completed ? (
                  <CheckCircleIcon sx={{ color: '#4caf50', fontSize: 28 }} />
                ) : step.inProgress ? (
                  <LinearProgress sx={{ width: 24 }} />
                ) : (
                  <InfoIcon sx={{ color: theme.palette.text.secondary, fontSize: 28 }} />
                )}
              </ListItemIcon>
              <ListItemText
                primary={step.label}
                primaryTypographyProps={{
                  sx: { fontWeight: 600, color: theme.palette.text.primary, fontSize: '15px' }
                }}
                secondary={step.completed ? 'Verified' : step.inProgress ? 'Verifying...' : 'Not verified'}
                secondaryTypographyProps={{
                  sx: { color: theme.palette.text.secondary, fontSize: '13px' }
                }}
                sx={{ flex: 1, minWidth: 0 }}
              />
              {step.completed ? (
                <Box sx={{ display: 'flex', alignItems: 'center', ml: 'auto' }}>
                  <Chip
                    icon={<CheckCircleIcon sx={{ color: '#fff !important' }} />}
                    label="Verified"
                    color="success"
                    sx={{
                      fontWeight: 600,
                      fontSize: '14px',
                      height: '36px',
                      px: 1.5,
                      backgroundColor: '#4caf50',
                      color: '#fff',
                      '& .MuiChip-label': {
                        px: 1
                      }
                    }}
                  />
                </Box>
              ) : tierDescriptions[step.id] ? (
                <Box sx={{ minWidth: 240, maxWidth: 280 }}>
                  <MUITextField
                    select
                    size="small"
                    label="What can I upload?"
                    value={selectedDocTypes[step.id] || ''}
                    onChange={(e) =>
                      setSelectedDocTypes((prev) => ({
                        ...prev,
                        [step.id]: e.target.value as string
                      }))
                    }
                    fullWidth
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderColor: '#F36409',
                        '& fieldset': {
                          borderColor: '#F36409',
                          borderWidth: '2px'
                        },
                        '&:hover fieldset': {
                          borderColor: '#F36409',
                          borderWidth: '2px'
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#F36409',
                          borderWidth: '2px'
                        }
                      }
                    }}
                  >
                    <MenuItem value="">
                      Select document type
                    </MenuItem>
                    {tierDescriptions[step.id].map((desc, descIndex) => (
                      <MenuItem key={descIndex} value={desc}>
                        {desc}
                      </MenuItem>
                    ))}
                  </MUITextField>
                </Box>
              ) : null}
              {!step.completed && !step.inProgress && (
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  {step.id.startsWith('tier') && prId ? (
                    <>
                      <input
                        ref={(el) => fileInputRefs.current[step.id] = el}
                        type="file"
                        multiple
                        accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                        style={{ display: 'none' }}
                        onChange={(e) => handleDocumentUpload(step.id, e.target.files)}
                      />
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => fileInputRefs.current[step.id]?.click()}
                        disabled={uploadingDocs[step.id] || !selectedDocTypes[step.id]}
                        startIcon={<UploadIcon />}
                        sx={{
                          textTransform: 'none',
                          borderColor: theme.palette.primary.main,
                          color: theme.palette.primary.main,
                          '&:hover': {
                            borderColor: theme.palette.primary.dark,
                            backgroundColor: theme.palette.mode === 'dark' ? 'rgba(58, 79, 237, 0.1)' : 'rgba(9, 39, 235, 0.05)'
                          },
                          '&:disabled': {
                            borderColor: theme.palette.divider,
                            color: theme.palette.text.disabled
                          }
                        }}
                      >
                        {uploadingDocs[step.id] ? 'Uploading...' : 'Upload Docs'}
                      </Button>
                      {/* Show optional badge for Tier 3 */}
                      
                    </>
                  ) : (
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => startVerification(index, step.id)}
                      sx={{
                        textTransform: 'none',
                        borderColor: theme.palette.primary.main,
                        color: theme.palette.primary.main,
                        '&:hover': {
                          borderColor: theme.palette.primary.dark,
                          backgroundColor: theme.palette.mode === 'dark' ? 'rgba(58, 79, 237, 0.1)' : 'rgba(9, 39, 235, 0.05)'
                        }
                      }}
                    >
                      Verify
                    </Button>
                  )}
                </Box>
              )}
              {step.documents && step.documents.length > 0 && (
                <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {step.documents.map((doc: string, docIndex: number) => (
                    <Chip
                      key={docIndex}
                      label={doc}
                      size="small"
                      sx={{ fontSize: '11px' }}
                    />
                  ))}
                </Box>
              )}
            </ListItem>
          ))}
        </List>
      </Box>
    </Paper>
  );
};

// Main PR Submission Component
export const PRSubmissionInterface: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { step } = useParams<{ step?: string }>();
  const location = useLocation();
  
  // Step to URL mapping
  const stepUrlMap: { [key: number]: string } = {
    1: 'upload-compose',
    2: 'ai-analysis',
    3: 'verification',
    4: 'review-submit'
  };
  
  // URL to step mapping
  const urlStepMap: { [key: string]: number } = {
    'upload-compose': 1,
    'ai-analysis': 2,
    'verification': 3,
    'review-submit': 4
  };
  
  // Get step from URL or default to 1
  const getStepFromUrl = () => {
    if (step && urlStepMap[step]) {
      return urlStepMap[step];
    }
    return 1;
  };
  
  const [currentStep, setCurrentStep] = useState(() => {
    // Initialize from URL if available, otherwise default to 1
    if (step && urlStepMap[step]) {
      return urlStepMap[step];
    }
    return 1;
  });
  const [prId, setPrId] = useState<string | null>(null);
  const [prContent, setPrContent] = useState<string>('');
  const [analysisData, setAnalysisData] = useState<any>(null);
  const [verificationComplete, setVerificationComplete] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Update step when URL changes
  useEffect(() => {
    if (step && urlStepMap[step]) {
      const stepFromUrl = urlStepMap[step];
      setCurrentStep(stepFromUrl);
    } else if (!step && location.pathname === '/create-pr') {
      // If no step in URL and we're at /create-pr, redirect to default
      navigate('/create-pr/upload-compose', { replace: true });
    }
  }, [step, location.pathname, navigate]);
  

  // Restore wizard progress if the user previously started a PR
  useEffect(() => {
    const savedPrId = localStorage.getItem('prId');
    const savedStep = localStorage.getItem('prCurrentStep');
    const savedVerification = localStorage.getItem('prVerificationComplete');
    const savedContent = localStorage.getItem('prContent');
    const isEditing = localStorage.getItem('isEditingPR') === 'true';

    if (savedPrId) {
      setPrId(savedPrId);
      
      // If editing, load the PR content and analysis data
      if (isEditing && savedContent) {
        setPrContent(savedContent);
      } else if (isEditing) {
        // Fetch PR content and analysis data if not in localStorage
        const fetchPRData = async () => {
          try {
            const { getPressRelease } = await import('../../services/prApi');
            const prData = await getPressRelease(savedPrId);
            if (prData.content) {
              setPrContent(prData.content);
              localStorage.setItem('prContent', prData.content);
            }
            // Load analysis data if available
            if (prData.aiAnalysis) {
              setAnalysisData(prData.aiAnalysis);
            }
          } catch (err) {
            console.error('Error fetching PR data:', err);
          }
        };
        fetchPRData();
      } else if (savedContent) {
        // Restore saved content even if not editing (for continuity)
        setPrContent(savedContent);
      }
    }

    if (savedVerification === 'true') {
      setVerificationComplete(true);
    }

    // Only restore step from localStorage if URL doesn't have a step
    if (!step) {
      if (savedStep) {
        const parsed = parseInt(savedStep, 10);
        // When editing, respect the saved step
        if (isEditing) {
          if (!isNaN(parsed) && parsed >= 1 && parsed <= 4) {
            const stepUrl = stepUrlMap[parsed];
            if (stepUrl) {
              navigate(`/create-pr/${stepUrl}`, { replace: true });
            }
          }
        } else {
          // For new PRs, don't restore step 4 - always reset to step 2 (AI Analysis) if saved step is 4
          if (parsed === 4) {
            // If saved step is 4, reset to step 2 (AI Analysis)
            navigate('/create-pr/ai-analysis', { replace: true });
            localStorage.setItem('prCurrentStep', '2');
          } else if (!isNaN(parsed) && parsed >= 1 && parsed <= 3) {
            const stepUrl = stepUrlMap[parsed];
            if (stepUrl) {
              navigate(`/create-pr/${stepUrl}`, { replace: true });
            }
          } else if (savedPrId) {
            // If there's a saved PR but invalid step, start at step 2 (AI Analysis)
            navigate('/create-pr/ai-analysis', { replace: true });
          }
        }
      } else if (savedPrId) {
        // If there's a saved PR but no saved step, start at step 2 (AI Analysis)
        navigate('/create-pr/ai-analysis', { replace: true });
      }
    }
    
    // Clear editing flag after use
    if (isEditing) {
      localStorage.removeItem('isEditingPR');
    }
  }, []);

  // Persist key wizard state so that users resume where they left off
  useEffect(() => {
    localStorage.setItem('prCurrentStep', String(currentStep));
  }, [currentStep]);

  useEffect(() => {
    if (prId) {
      localStorage.setItem('prId', prId);
    } else {
      localStorage.removeItem('prId');
    }
  }, [prId]);

  useEffect(() => {
    localStorage.setItem('prVerificationComplete', verificationComplete ? 'true' : 'false');
  }, [verificationComplete]);

  // Save PR content to localStorage whenever it changes
  useEffect(() => {
    if (prContent) {
      localStorage.setItem('prContent', prContent);
    }
  }, [prContent]);

  const steps = [
    'Upload & Compose',
    'AI Analysis',
    'Verification',
    'Review & Submit'
  ];

  const handleUploadSuccess = (data: any) => {
    setPrId(data.prId);
    setPrContent(data.content);
    setSuccess('Files uploaded successfully!');
  };

  const handleAnalysisComplete = (data: any) => {
    setAnalysisData(data);
    setSuccess('Analysis completed successfully!');
  };

  const handleVerificationComplete = (data: any) => {
    setVerificationComplete(true);
    setSuccess('Verification completed successfully!');
    // Create an in-app notification so it appears in Notifications & Timeline
    notifyVerificationApproved().catch((err) => {
      console.error('Failed to send verification notification', err);
    });
  };

  const handleEditPR = () => {
    // Navigate back to step 1 (Upload & Compose) to edit PR
    navigate('/create-pr/upload-compose');
  };

  const handleNext = () => {
    if (currentStep === 1 && !prId && !prContent) {
      setError('Please upload files or enter content before proceeding');
      return;
    }
    if (currentStep === 2 && !analysisData) {
      setError('Please complete analysis before proceeding');
      return;
    }
    if (currentStep === 3 && !verificationComplete) {
      setError('Please complete all required verification steps before proceeding (Stage 3 is optional)');
      return;
    }
    setError(null);
    const nextStep = Math.min(currentStep + 1, steps.length);
    const nextStepUrl = stepUrlMap[nextStep];
    if (nextStepUrl) {
      navigate(`/create-pr/${nextStepUrl}`);
    }
  };

  const handleBack = () => {
    const prevStep = Math.max(currentStep - 1, 1);
    const prevStepUrl = stepUrlMap[prevStep];
    if (prevStepUrl) {
      navigate(`/create-pr/${prevStepUrl}`);
    }
  };

  const handleFinalSubmit = async () => {
    setSubmitting(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      
      const response = await axios.post(
        `${API_BASE_URL}/pr/submit`,
        {
          prId,
          analysisData,
          verified: verificationComplete
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      // Get the PR ID from response (use submissionId or pressRelease._id)
      const submittedPrId = response.data?.submissionId || response.data?.pressRelease?._id || response.data?.pressRelease?.id || prId;
      
      if (!submittedPrId) {
        throw new Error('No PR ID returned from server');
      }

      // Fire-and-forget: create a PR submitted notification for this user
      notifyPRSubmitted('Your press release', submittedPrId).catch((err) => {
        console.error('Failed to send PR submitted notification', err);
      });

      setSuccess('Press Release submitted successfully! Redirecting to Distribution...');
      
      // Clear all PR-related localStorage to reset form for next PR
      localStorage.removeItem('prId');
      localStorage.removeItem('prCurrentStep');
      localStorage.removeItem('prContent');
      localStorage.removeItem('prVerificationComplete');
      localStorage.removeItem('isEditingPR');
      
      // Reset form state to step 1
      setPrId(null);
      setPrContent('');
      setAnalysisData(null);
      setVerificationComplete(false);
      
      // Redirect to Distribution page after submission using React Router
      setTimeout(() => {
        navigate(`/distribute?prId=${submittedPrId}`);
      }, 2000);

    } catch (err: any) {
      console.error('Submission error:', err);
      setError(err.response?.data?.message || 'Failed to submit PR. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ 
      maxWidth: '1200px', 
      mx: 'auto', 
      p: { xs: 1.5, md: 2 },
      backgroundColor: theme.palette.background.default,
      minHeight: '100vh',
    }}>
      {/* Global Error/Success Snackbar - Bold and Clear */}
      <Snackbar
        open={!!error}
        autoHideDuration={8000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          severity="error" 
          onClose={() => setError(null)} 
          sx={{ 
            width: '100%',
            fontWeight: 700,
            fontSize: '16px',
            '& .MuiAlert-message': {
              fontWeight: 700,
              fontSize: '16px'
            },
            '& .MuiAlert-icon': {
              fontSize: '28px'
            }
          }}
        >
          <strong>{error}</strong>
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={() => setSuccess(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          severity="success" 
          onClose={() => setSuccess(null)} 
          sx={{ 
            width: '100%',
            fontWeight: 700,
            fontSize: '16px',
            '& .MuiAlert-message': {
              fontWeight: 700,
              fontSize: '16px'
            },
            '& .MuiAlert-icon': {
              fontSize: '28px'
            }
          }}
        >
          <strong>{success}</strong>
        </Alert>
      </Snackbar>

      {/* Header Section */}
      <Box sx={{ mb: 2 }}>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            color: theme.palette.text.primary,
            mb: 0.5,
            fontSize: { xs: '24px', md: '32px' }
          }}
        >
          PR Submission & Verification
        </Typography>
        <Typography variant="body1" sx={{ color: theme.palette.primary.main, fontSize: '15px' }}>
          Submit your press release and complete verification.
        </Typography>
      </Box>

      {/* Progress Steps */}
      <Paper
        elevation={0}
        sx={{
          mb: 2,
          p: 2,
          backgroundColor: theme.palette.background.paper,
          borderRadius: '12px',
          border: `1px solid ${theme.palette.divider}`
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, overflowX: 'auto', pb: 1 }}>
          {steps.map((step, index) => (
            <React.Fragment key={index}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  minWidth: { xs: '60px', sm: '80px' },
                  flex: 1
                }}
              >
                <Box
                  sx={{
                    width: { xs: 28, sm: 36 },
                    height: { xs: 28, sm: 36 },
                    borderRadius: '50%',
                    backgroundColor: index + 1 <= currentStep 
                      ? theme.palette.primary.main 
                      : (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : '#e0e0e0'),
                    color: index + 1 <= currentStep 
                      ? theme.palette.primary.contrastText 
                      : theme.palette.text.secondary,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 600,
                    fontSize: { xs: '12px', sm: '14px' },
                    mb: 0.5,
                    transition: 'all 0.3s'
                  }}
                >
                  {index + 1}
                </Box>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: index + 1 === currentStep ? 600 : 500,
                    color: index + 1 === currentStep 
                      ? theme.palette.text.primary 
                      : theme.palette.text.secondary,
                    fontSize: { xs: '10px', sm: '12px' },
                    textAlign: 'center',
                    whiteSpace: { xs: 'nowrap', sm: 'normal' }
                  }}
                >
                  {step}
                </Typography>
              </Box>
              {index < steps.length - 1 && (
                <Box
                  sx={{
                    flex: 0.5,
                    height: 2,
                    backgroundColor: index + 1 < currentStep 
                      ? theme.palette.primary.main 
                      : (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : '#e0e0e0'),
                    mt: -2.5,
                    transition: 'all 0.3s',
                    display: { xs: 'none', sm: 'block' }
                  }}
                />
              )}
            </React.Fragment>
          ))}
        </Box>
      </Paper>

      {/* Step Content */}
      {currentStep === 1 && (
        <PRUploadInterface 
          onUploadSuccess={handleUploadSuccess}
          onContentChange={(content) => setPrContent(content)}
          initialContent={prContent}
        />
      )}
      {currentStep === 2 && (
        <AIAnalysisInterface 
          prId={prId || undefined}
          content={prContent}
          onAnalysisComplete={handleAnalysisComplete}
          onEditPR={handleEditPR}
        />
      )}
      {currentStep === 3 && (
        <VerificationInterface 
          prId={prId || undefined}
          prContent={prContent}
          analysisData={analysisData}
          onVerificationComplete={handleVerificationComplete}
        />
      )}
      {currentStep === 4 && (
        <Paper
          elevation={0}
          sx={{
            p: 3,
            backgroundColor: theme.palette.background.paper,
            borderRadius: '12px',
            border: `1px solid ${theme.palette.divider}`,
            textAlign: 'center'
          }}
        >
          <CheckCircleIcon sx={{ fontSize: 64, color: '#4caf50', mb: 2 }} />
          <Typography variant="h5" sx={{ fontWeight: 600, color: theme.palette.text.primary, mb: 1 }}>
            Ready to Submit
          </Typography>
          <Typography variant="body1" sx={{ color: theme.palette.text.secondary, mb: 3 }}>
            Your PR has been analyzed and verified. Click submit to proceed with distribution.
          </Typography>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <Button
            variant="contained"
            size="large"
            onClick={handleFinalSubmit}
            disabled={submitting}
            sx={{
              backgroundColor: theme.palette.mode === 'dark' ? theme.palette.primary.main : '#000000',
              color: '#ffffff',
              fontWeight: 600,
              textTransform: 'none',
              px: 4,
              py: 1.5,
              fontSize: '16px',
              borderRadius: '8px',
              boxShadow: 'none',
              '&:hover': {
                backgroundColor: theme.palette.mode === 'dark' ? theme.palette.primary.dark : '#1a1a1a',
                boxShadow: 'none'
              },
              '&:disabled': {
                backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : '#f0f0f0',
                color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.3)' : '#d0d0d0'
              }
            }}
          >
            {submitting ? 'Submitting...' : 'Submit Press Release'}
          </Button>
        </Paper>
      )}

      {/* Navigation Buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
        <Button
          variant="outlined"
          onClick={handleBack}
          disabled={currentStep === 1}
          sx={{
            textTransform: 'none',
            px: 3,
            py: 1,
            fontSize: '14px',
            fontWeight: 600,
            borderRadius: '8px',
            borderColor: theme.palette.divider,
            color: theme.palette.text.primary,
            '&:hover': {
              borderColor: theme.palette.primary.main,
              backgroundColor: theme.palette.mode === 'dark' ? 'rgba(58, 79, 237, 0.1)' : 'rgba(9, 39, 235, 0.05)'
            },
            '&:disabled': {
              borderColor: theme.palette.divider,
              color: theme.palette.text.disabled
            }
          }}
        >
          Back
        </Button>
        <Button
          variant="contained"
          onClick={handleNext}
          disabled={currentStep === steps.length}
          sx={{
            backgroundColor: '#F36409',
            color: '#ffffff',
            fontWeight: 600,
            textTransform: 'none',
            px: 3,
            py: 1,
            fontSize: '14px',
            borderRadius: '8px',
            boxShadow: 'none',
            '&:hover': {
              backgroundColor: '#d85608',
              boxShadow: 'none'
            },
            '&:disabled': {
              backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : '#f0f0f0',
              color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.3)' : '#d0d0d0'
            }
          }}
        >
          Next
        </Button>
      </Box>
    </Box>
  );
};

